import React from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Star } from "lucide-react";
import { getOperation, LEVELS, getFractionSubOp, getDecimalSubOp, getAlgebraSubOp, getGeometriaSubOp } from "../lib/constants";
import { ProfileBar } from "../components/ProfileBar";
import { PageShell } from "../components/PageShell";
import { ClayButton } from "../components/ClayButton";

export default function LevelSelect({ profile, onReset }) {
  const { operationId, subOp } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const isAlgMode = location.pathname.startsWith("/secundaria/algebra");
  const isGeomMode = location.pathname.startsWith("/secundaria/geometria");
  const isSecundariaMode = isAlgMode || isGeomMode;
  const isDecMode = !isSecundariaMode && location.pathname.startsWith("/practica/decimales");
  const isFracMode = !isSecundariaMode && !isDecMode && !!subOp;

  const sub = isAlgMode
    ? getAlgebraSubOp(subOp)
    : isGeomMode
    ? getGeometriaSubOp(subOp)
    : isFracMode
    ? getFractionSubOp(subOp)
    : isDecMode
    ? getDecimalSubOp(subOp)
    : null;
  const op = isSecundariaMode
    ? getOperation("fracciones") /* placeholder; we use `sub` colors for secundaria */
    : isFracMode
    ? getOperation("fracciones")
    : isDecMode
    ? getOperation("decimales")
    : getOperation(operationId);

  if ((!op && !isSecundariaMode) || ((isFracMode || isDecMode || isSecundariaMode) && !sub)) {
    navigate("/dashboard");
    return null;
  }

  const isSubMode = isFracMode || isDecMode || isSecundariaMode;
  const headerColor = isSubMode ? sub.color : op.color;
  const headerBorder = isSubMode ? sub.borderDark : op.borderDark;
  const headerLight = isSubMode ? sub.colorLight : op.colorLight;
  const headerSymbol = isSubMode ? sub.symbol : op.symbol;
  const headerName = isAlgMode
    ? `Álgebra · ${sub.short}`
    : isGeomMode
    ? `Geometría · ${sub.short}`
    : isFracMode
    ? `Fracciones · ${sub.short}`
    : isDecMode
    ? `Decimales · ${sub.short}`
    : op.name;
  const backTarget = isAlgMode
    ? "/secundaria/algebra"
    : isGeomMode
    ? "/secundaria/geometria"
    : isFracMode
    ? "/practica/fracciones"
    : isDecMode
    ? "/practica/decimales"
    : "/dashboard";

  const buildTarget = (lvlId) =>
    isAlgMode
      ? `/secundaria/algebra/${subOp}/${lvlId}`
      : isGeomMode
      ? `/secundaria/geometria/${subOp}/${lvlId}`
      : isFracMode
      ? `/practica/fracciones/${subOp}/${lvlId}`
      : isDecMode
      ? `/practica/decimales/${subOp}/${lvlId}`
      : `/practica/${operationId}/${lvlId}`;

  return (
    <PageShell>
      <ProfileBar profile={profile} onReset={onReset} />

      <div className="mt-6">
        <ClayButton variant="ghost" onClick={() => navigate(backTarget)} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" strokeWidth={3} />
          Volver
        </ClayButton>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-2"
      >
        <div
          className="inline-flex items-center gap-3 rounded-full px-4 py-2 mb-4"
          style={{ backgroundColor: headerLight }}
        >
          <span
            className="w-9 h-9 rounded-xl flex items-center justify-center font-heading text-2xl font-bold text-white"
            style={{ backgroundColor: headerColor }}
          >
            {headerSymbol}
          </span>
          <span className="font-bold text-base" style={{ color: headerBorder }}>
            {headerName}
          </span>
        </div>

        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1C1C1E] mb-2">
          Elige tu nivel
        </h2>
        <p className="text-base sm:text-lg text-[#5C5C60] mb-8 max-w-xl">
          Cada nivel da más puntos. ¡Elige uno y empieza la ronda de 10 preguntas!
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6" data-testid="levels-grid">
          {LEVELS.map((lvl, i) => (
            <motion.button
              key={lvl.id}
              onClick={() => navigate(buildTarget(lvl.id))}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, type: "spring", bounce: 0.4 }}
              whileHover={{ y: -6 }}
              whileTap={{ scale: 0.97 }}
              className="clay-btn relative rounded-[2rem] p-6 text-center bg-white border-4 border-b-[8px] active:border-b-4 transition-all"
              style={{ borderColor: lvl.color, borderBottomColor: lvl.color }}
              data-testid={`level-${lvl.id}`}
            >
              <div className="flex items-center justify-center gap-1 mb-3">
                {Array.from({ length: 3 }).map((_, idx) => (
                  <Star
                    key={idx}
                    className="w-7 h-7"
                    strokeWidth={2.5}
                    style={{
                      color: idx < lvl.stars ? lvl.color : "#E5E7EB",
                      fill: idx < lvl.stars ? lvl.color : "transparent",
                    }}
                  />
                ))}
              </div>
              <h3 className="font-heading text-3xl font-bold text-[#1C1C1E] mb-1">{lvl.name}</h3>
              <p className="text-sm font-bold uppercase tracking-wider" style={{ color: lvl.color }}>
                +{lvl.points} pts por acierto
              </p>
            </motion.button>
          ))}
        </div>
      </motion.div>
    </PageShell>
  );
}
